import {
  View,
  Text,
  StyleSheet,
} from "react-native";
import React, { useEffect } from "react";

import ClientSummary from "./Summary/ClientSummary";
import ClientPlanification from "./Planification/ClientPlanification";
import ClientCalendar from "./Calendar/ClientCalendar";
import ClientStatistics from "./Statistics/ClientStatistics";
import ClientProfile from "./Profile/ClientProfile";

import GlobalStyles from "../../Resources/Styles";

import { observer, Observer } from "mobx-react";
import { useScreenDimensions } from "./../_layout";
import ClientForms from "./Forms/ClientForms";

import { useTranslation } from "react-i18next";
import CustomActivityIndicator from "../Components/CustomActivityIndicator";
import SyncStatusIndicator from "../Components/SyncStatusIndicator";
import LogoImage from "../Components/LogoImage";
import ClientNutrition from "./Nutrition/ClientNutrition";
import GlobalExerciseSearchFloating from "../Exercise/Search/GlobalExerciseSearchFloating";
import UnifiedTabBar from "../Components/UnifiedTabBar";

const ClientDashboard = observer(
  ({
    client,
    requestProfileLeaveAwareNavigation = null,
    profileDiscardResetVersion = 0,
  }) => {
  const { t } = useTranslation();

  // Check if there are unseen forms (needs to be in observer component for reactivity)
  const hasUnseenForms = client.forms.some((form) => !form.getSeenByCoach);

  const TabOptions = client.allowedNutrition
    ? [
        "summary-string",
        "planification-string",
        "calendar-string",
        "forms-string",
        "stats-string",
        "nutrition-string",
        "profile-string",
      ]
    : [
        "summary-string",
        "planification-string",
        "calendar-string",
        "forms-string",
        "stats-string",
        "profile-string",
      ];
  const components = {
    "summary-string": ClientSummary,
    "planification-string": ClientPlanification,
    "calendar-string": ClientCalendar,
    "forms-string": ClientForms,
    "stats-string": ClientStatistics,
    "nutrition-string": ClientNutrition,
    "profile-string": ClientProfile,
  };
  const ComponentToShow = components[client.selectedTab] || null;

  const { screenSize } = useScreenDimensions();
  const runWithProfileLeaveGuard = (action) => {
    if (requestProfileLeaveAwareNavigation) {
      requestProfileLeaveAwareNavigation(action);
      return;
    }

    action();
  };

  console.log("clientDashboard refreshed");

  useEffect(() => {
    return () => {
      console.log("ClientDashboard unmounted", client.name);
      if (client?.coach?.selectedChatId != client.id) {
        client.firestoreListener.removeBlocksListener();
        client.syncStatusManager.setOperationStatus("listenerBlocks", null);
      }

      client.firestoreListener.removeFormsListener();
      client.syncStatusManager.setOperationStatus("listenerForms", null);
    };
  }, [client]);

  return (
    <>
      {client.isLoading ? (
        <CustomActivityIndicator />
      ) : (
        <View style={[styles.container, { flex: 1 }]}>
          <View style={{ height: "100%" }}>
            {client && (
                <View
                  style={[
                    GlobalStyles.HStack,
                    {
                      alignItems: "center",
                    },
                  ]}
                >
                <View style={{ flex: 1, minWidth: 0 }}>
                  <View
                    style={[
                      GlobalStyles.HStack,
                      {
                        flex: 1,
                        minWidth: 0,
                        justifyContent: "flex-start",
                        alignItems: "center",
                      },
                    ]}
                  >
                    <Observer>
                      {() => (
                        <Text
                          style={[
                            styles.title,
                            {
                              marginRight: screenSize === "small" ? 8 : 16,
                              flexShrink: 1,
                              minWidth: 0,
                            },
                          ]}
                          numberOfLines={1}
                        >
                          {client.nameForCoach}
                        </Text>
                      )}
                    </Observer>
                    <View style={{ flexShrink: 0 }}>
                      <SyncStatusIndicator
                        syncStatusManager={client.syncStatusManager}
                        onPress={() => client.performTransaction()}
                      />
                    </View>
                  </View>
                  <Text
                    style={[
                      styles.subtitle,
                      { fontSize: 14, marginTop: 4, fontWeight: "400" },
                    ]}
                  >
                    {client.email}
                  </Text>
                </View>
                {client.coach && client.coach.logoUrl && (
                  <View style={{ marginLeft: 16 }}>
                    <LogoImage coach={client.coach} />
                  </View>
                )}
              </View>
            )}

            <UnifiedTabBar
              tabs={TabOptions.map((option) => ({
                key: option,
                label: t(option),
                showDot: option === "forms-string" && hasUnseenForms,
              }))}
              selectedKey={client.selectedTab}
              onSelect={(key) =>
                runWithProfileLeaveGuard(() =>
                  client.setProperty("selectedTab", key)
                )
              }
              topMargin={30}
              itemGap={screenSize === "small" ? 12 : 24}
            />

            {ComponentToShow &&
              (client.selectedTab === "profile-string" ? (
                <ComponentToShow
                  client={client}
                  profileDiscardResetVersion={profileDiscardResetVersion}
                />
              ) : (
                <ComponentToShow client={client} />
              ))}
          </View>
          
          {/* Global floating search - single instance for all exercises and slots */}
          <GlobalExerciseSearchFloating />
        </View>
      )}
    </>
  );
  }
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  title: {
    fontSize: 24,
    fontWeight: "600",
  },
  subtitle: {
    fontSize: 18,
    fontWeight: "400",
    color: "gray",
  },
});

export default ClientDashboard;
